// Enhanced Permission Manager for Granular RBAC Control
import { RoleLevel, ENHANCED_ROLE_PERMISSIONS, buildRolePermissions, getPermissionsByCategory, PERMISSION_METADATA, ComponentPermissions, ActionPermissions } from '../types/rbacImproved';
export class PermissionManager {
    /**
     * Get all permissions for a specific role level
     */
    static getRolePermissions(roleLevel) {
        return this.rolePermissions[roleLevel] || [];
    }
    /**
     * Check if a role has a specific permission
     */
    static roleHasPermission(roleLevel, permission) {
        return this.getRolePermissions(roleLevel).includes(permission);
    }
    /**
     * Get permissions that are directly assigned to a role (not inherited)
     */
    static getDirectRolePermissions(roleLevel) {
        return ENHANCED_ROLE_PERMISSIONS[roleLevel] || [];
    }
    /**
     * Get permissions inherited from lower roles
     */
    static getInheritedPermissions(roleLevel) {
        const directPermissions = this.getDirectRolePermissions(roleLevel);
        const allPermissions = this.getRolePermissions(roleLevel);
        return allPermissions.filter(p => !directPermissions.includes(p));
    }
    /**
     * Get permission metadata
     */
    static getPermissionInfo(permission) {
        const metadata = PERMISSION_METADATA[permission];
        return {
            permission,
            label: permission.split('.').pop()?.replace(/_/g, ' ') || permission,
            description: metadata?.description || 'No description available',
            category: metadata?.category || 'Unknown',
            riskLevel: metadata?.riskLevel || 'low'
        };
    }
    /**
     * Get all permissions grouped by category
     */
    static getPermissionsByCategory() {
        return getPermissionsByCategory();
    }
    /**
     * Calculate effective permissions with overrides
     */
    static calculateEffectivePermissions(baseRole, additionalPermissions = [], revokedPermissions = [], temporaryPermissions = []) {
        // Start with base role permissions
        let permissions = this.getRolePermissions(baseRole);
        // Add additional permissions
        permissions = [...permissions, ...additionalPermissions];
        // Add active temporary permissions
        const activeTemporary = temporaryPermissions
            .filter(tp => tp.isActive && new Date() < tp.expiresAt)
            .map(tp => tp.permission);
        permissions = [...permissions, ...activeTemporary];
        // Remove revoked permissions
        permissions = permissions.filter(p => !revokedPermissions.includes(p));
        // Remove duplicates
        return [...new Set(permissions)];
    }
    /**
     * Get permission differences between two roles
     */
    static getPermissionDifferences(fromRole, toRole) {
        const fromPermissions = this.getRolePermissions(fromRole);
        const toPermissions = this.getRolePermissions(toRole);
        return {
            added: toPermissions.filter(p => !fromPermissions.includes(p)),
            removed: fromPermissions.filter(p => !toPermissions.includes(p)),
            unchanged: fromPermissions.filter(p => toPermissions.includes(p))
        };
    }
    /**
     * Validate permission override request
     */
    static validatePermissionRequest(userRole, requestedPermission, requestingAdminRole) {
        const permissionInfo = this.getPermissionInfo(requestedPermission);
        const userHasPermission = this.roleHasPermission(userRole, requestedPermission);
        // If user already has permission, no need for override
        if (userHasPermission) {
            return {
                isValid: false,
                reason: 'User already has this permission through their role',
                riskLevel: permissionInfo.riskLevel,
                requiresApproval: false
            };
        }
        // Check if requesting admin can grant this permission
        const adminCanGrant = this.roleHasPermission(requestingAdminRole, requestedPermission);
        if (!adminCanGrant) {
            return {
                isValid: false,
                reason: 'Requesting admin does not have this permission to grant',
                riskLevel: permissionInfo.riskLevel,
                requiresApproval: false
            };
        }
        // Determine if approval is needed based on risk level
        const requiresApproval = permissionInfo.riskLevel === 'critical' ||
            (permissionInfo.riskLevel === 'high' && requestingAdminRole < RoleLevel.SUPER_ADMIN);
        return {
            isValid: true,
            riskLevel: permissionInfo.riskLevel,
            requiresApproval
        };
    }
    /**
     * Get suggested role for a set of permissions
     */
    static suggestRoleForPermissions(requiredPermissions) {
        // Find the lowest role that has all required permissions
        for (let role = RoleLevel.GUEST; role <= RoleLevel.SUPER_ADMIN; role++) {
            const rolePermissions = this.getRolePermissions(role);
            const missingPermissions = requiredPermissions.filter(p => !rolePermissions.includes(p));
            if (missingPermissions.length === 0) {
                return {
                    suggestedRole: role,
                    missingPermissions: []
                };
            }
            if (role === RoleLevel.SUPER_ADMIN) {
                return {
                    suggestedRole: role,
                    missingPermissions
                };
            }
        }
        return {
            suggestedRole: RoleLevel.SUPER_ADMIN,
            missingPermissions: requiredPermissions
        };
    }
    /**
     * Get permissions that would be lost when downgrading a role
     */
    static getPermissionsLostOnDowngrade(fromRole, toRole) {
        if (toRole >= fromRole)
            return [];
        const fromPermissions = this.getRolePermissions(fromRole);
        const toPermissions = this.getRolePermissions(toRole);
        return fromPermissions.filter(p => !toPermissions.includes(p));
    }
    /**
     * Generate permission report for a user
     */
    static generatePermissionReport(userRole, additionalPermissions = [], revokedPermissions = []) {
        const basePermissions = this.getRolePermissions(userRole);
        const effectivePermissions = this.calculateEffectivePermissions(userRole, additionalPermissions, revokedPermissions);
        const categories = this.getPermissionsByCategory();
        const report = {
            summary: {
                totalPermissions: effectivePermissions.length,
                basePermissions: basePermissions.length,
                additionalPermissions: additionalPermissions.length,
                revokedPermissions: revokedPermissions.length,
                inheritedPermissions: this.getInheritedPermissions(userRole).length,
                directPermissions: this.getDirectRolePermissions(userRole).length
            },
            byCategory: {},
            riskAnalysis: {
                low: 0,
                medium: 0,
                high: 0,
                critical: 0
            }
        };
        // Analyze by category
        Object.entries(categories).forEach(([categoryName, permissions]) => {
            let hasPermissionsCount = 0;
            const permissionAnalysis = permissions.map(permInfo => {
                const hasAccess = effectivePermissions.includes(permInfo.permission);
                if (hasAccess) {
                    report.riskAnalysis[permInfo.riskLevel]++;
                    hasPermissionsCount++;
                }
                let source = 'inherited';
                if (revokedPermissions.includes(permInfo.permission)) {
                    source = 'revoked';
                }
                else if (additionalPermissions.includes(permInfo.permission)) {
                    source = 'additional';
                }
                else if (this.getDirectRolePermissions(userRole).includes(permInfo.permission)) {
                    source = 'direct';
                }
                return {
                    permission: permInfo.permission,
                    hasAccess,
                    source
                };
            });
            const categoryAnalysis = {
                hasPermissions: hasPermissionsCount,
                totalPermissions: permissions.length,
                permissions: permissionAnalysis
            };
            report.byCategory[categoryName] = categoryAnalysis;
        });
        return report;
    }
    /**
     * Validate a complete permission configuration
     */
    static validatePermissionConfiguration(permissions) {
        const validationWarnings = [];
        const validationSuggestions = [];
        // Check for suspicious permission combinations
        const hasUserDelete = permissions.includes(ActionPermissions.DELETE_USER);
        const hasUserCreate = permissions.includes(ActionPermissions.CREATE_USER);
        if (hasUserDelete && !hasUserCreate) {
            validationWarnings.push('User has delete permissions but not create permissions - unusual combination');
        }
        // Check for missing dependencies
        const hasAdvancedSearch = permissions.includes(ComponentPermissions.USE_ADVANCED_SEARCH);
        const hasBasicSearch = permissions.includes(ComponentPermissions.USE_BASIC_SEARCH);
        if (hasAdvancedSearch && !hasBasicSearch) {
            validationSuggestions.push('Consider adding basic search permission as a foundation for advanced search');
        }
        // Check risk profile
        const criticalPermissions = permissions.filter(p => PERMISSION_METADATA[p]?.riskLevel === 'critical');
        if (criticalPermissions.length > 0) {
            validationWarnings.push(`User has ${criticalPermissions.length} critical permissions - requires careful monitoring`);
        }
        return {
            isValid: validationWarnings.length === 0,
            warnings: validationWarnings,
            suggestions: validationSuggestions
        };
    }
}
PermissionManager.rolePermissions = buildRolePermissions();
